/**
 * One definition of "the browser lost the connection", shared by client
 * capture (captureClientError downgrades it to transient), the streaming job
 * store (never retries it), and event polling (never reports it).
 *
 * Import-free so it is safe on both sides of the client/server boundary.
 * extensionFrames.ts keeps its own anchored regex on purpose: it inspects
 * Sentry's serialized exception value, where a substring match would let an
 * extension's unrelated "network error" escape the extension filter.
 */

const MAX_CAUSE_DEPTH = 5;

/**
 * A dropped connection, as each browser words it: Chrome "Failed to fetch"
 * (or "network error" when the drop hits mid-stream), Safari "Load failed",
 * Firefox "NetworkError when attempting to fetch resource.". The whole message
 * must match, optionally prefixed "TypeError: " (a stringified error) and
 * suffixed " (host)" (Sentry's fetch instrumentation): an app error that merely contains the wording
 * ("Failed to fetch result card image") is a real failure, not a drop. The
 * request may or may not have reached the server.
 */
const NETWORK_DROP_MESSAGE =
  /^(typeerror: )?(failed to fetch|load failed|networkerror when attempting to fetch resource\.|network error)( \(.+\))?$/i;

export function isNetworkDropMessage(message: string): boolean {
  return NETWORK_DROP_MESSAGE.test(message.trim());
}

/**
 * True when the thrown value, or an error in its `cause` chain, is a network
 * drop. A tRPC error carrying a server response (`data`) is never a drop: the
 * server answered, so any network wording in it came from the server.
 */
export function isNetworkDropError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
    if (typeof current === "string") return isNetworkDropMessage(current);
    if (typeof current !== "object" || current === null) return false;
    const { message, data, cause } = current as {
      message?: unknown;
      data?: unknown;
      cause?: unknown;
    };
    if (data != null) return false;
    if (typeof message === "string" && isNetworkDropMessage(message)) return true;
    if (cause === current) return false;
    current = cause;
  }
  return false;
}
